import { useEffect, useState, useCallback } from "react";
import { Send, ArrowRightLeft, Check, X, Loader2, Building, Inbox } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-800",
  accepted: "bg-emerald-100 text-emerald-800",
  declined: "bg-red-100 text-red-800",
  cancelled: "bg-slate-100 text-slate-600",
};

export const CrossDistrictTransfers = () => {
  const [directory, setDirectory] = useState([]);
  const [schools, setSchools] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [form, setForm] = useState({ school: "", student: "", corp: "", note: "" });
  const [students, setStudents] = useState([]);
  const [sending, setSending] = useState(false);
  const [acceptFor, setAcceptFor] = useState(null);
  const [acceptSchool, setAcceptSchool] = useState("");
  const [acceptGrade, setAcceptGrade] = useState("");
  const [accepting, setAccepting] = useState(false);

  const load = useCallback(() => {
    api.get("/corporate/directory").then(({ data }) => setDirectory(data)).catch(() => {});
    api.get("/corporate/transfer-options").then(({ data }) => setSchools(data)).catch(() => {});
    api.get("/corporate/transfer-requests").then(({ data }) => setRequests(data)).catch(() => {});
    window.dispatchEvent(new CustomEvent("logerd:xfers-updated"));
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    if (!form.school) { setStudents([]); return; }
    api.get(`/corporate/school-students?company_id=${form.school}`)
      .then(({ data }) => setStudents(data))
      .catch(() => setStudents([]));
  }, [form.school]);

  const sendRequest = async (e) => {
    e.preventDefault();
    if (!form.student || !form.corp) return;
    setSending(true);
    try {
      await api.post("/corporate/transfer-requests", {
        student_id: form.student,
        to_corporate_id: form.corp,
        note: form.note,
      });
      toast.success("Transfer request sent — the receiving district must accept it");
      setForm({ school: form.school, student: "", corp: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send request");
    } finally {
      setSending(false);
    }
  };

  const accept = async () => {
    if (!acceptFor || !acceptSchool || accepting) return;
    setAccepting(true);
    try {
      const { data } = await api.post(`/corporate/transfer-requests/${acceptFor.id}/accept`, {
        to_company_id: acceptSchool,
        to_class_id: acceptGrade,
      });
      toast.success(`${acceptFor.student_name} transferred to ${data.to_school_name}`);
      setAcceptFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Accept failed");
    } finally {
      setAccepting(false);
    }
  };

  const resolve = async (rid, action) => {
    try {
      await api.post(`/corporate/transfer-requests/${rid}/${action}`);
      toast.success(action === "decline" ? "Request declined" : "Request cancelled");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Action failed");
    }
  };

  const acceptGrades = schools.find((s) => s.id === acceptSchool)?.grades || [];
  const pendingIncoming = requests.incoming.filter((r) => r.status === "pending").length;

  return (
    <section className="mt-8 rounded-2xl border border-indigo-200 bg-indigo-50/40 overflow-hidden shadow-sm" data-testid="cross-district-section">
      <div className="px-5 py-4 border-b border-indigo-200/60">
        <div className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4 text-indigo-600" /> Between districts
          {pendingIncoming > 0 && (
            <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white" data-testid="xfer-incoming-badge">
              {pendingIncoming} to review
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500">Send a student to another district — they must accept before the student moves. Incoming requests appear below.</div>
      </div>

      <form onSubmit={sendRequest} className="grid gap-2 px-5 py-4 border-b border-indigo-200/40 md:grid-cols-5" data-testid="xfer-request-form">
        <select value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value, student: "" })}
          data-testid="xfer-from-school" className="h-10 rounded-lg border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-primary">
          <option value="">From school…</option>
          {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={form.student} onChange={(e) => setForm({ ...form, student: e.target.value })} disabled={!form.school}
          data-testid="xfer-student" className="h-10 rounded-lg border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-primary disabled:opacity-50">
          <option value="">Student…</option>
          {students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
        </select>
        <select value={form.corp} onChange={(e) => setForm({ ...form, corp: e.target.value })}
          data-testid="xfer-to-corp" className="h-10 rounded-lg border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-primary">
          <option value="">To district…</option>
          {directory.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)"
          data-testid="xfer-note" className="h-10 rounded-lg border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-primary" />
        <button type="submit" disabled={sending || !form.student || !form.corp} data-testid="xfer-send-btn"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 h-10 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 transition">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send request
        </button>
      </form>

      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-indigo-200/40">
        <div data-testid="xfer-incoming-list">
          <div className="px-5 pt-4 pb-2 text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Inbox className="h-3.5 w-3.5" /> Incoming requests
          </div>
          {requests.incoming.length === 0 && <div className="px-5 pb-5 text-sm text-slate-500">No incoming requests.</div>}
          <ul className="px-5 pb-4 space-y-2">
            {requests.incoming.map((r) => (
              <li key={r.id} className="rounded-lg bg-white border border-slate-200 px-3.5 py-2.5" data-testid={`xfer-incoming-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{r.student_name}</div>
                    <div className="text-xs text-slate-500 truncate">from {r.from_school_name} · {r.from_corporate_name}</div>
                    {r.note && <div className="text-xs text-slate-600 italic mt-0.5 truncate">“{r.note}”</div>}
                  </div>
                  {r.status === "pending" ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => { setAcceptFor(r); setAcceptSchool(""); setAcceptGrade(""); }}
                        data-testid={`xfer-accept-${r.id}`} title="Accept and choose destination school"
                        className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition">
                        <Check className="h-3.5 w-3.5" /> Accept
                      </button>
                      <button onClick={() => resolve(r.id, "decline")} data-testid={`xfer-decline-${r.id}`} title="Decline"
                        className="inline-flex items-center rounded-lg border border-red-200 bg-white px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_CLS[r.status]}`}>{r.status}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div data-testid="xfer-outgoing-list">
          <div className="px-5 pt-4 pb-2 text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Building className="h-3.5 w-3.5" /> Outgoing requests
          </div>
          {requests.outgoing.length === 0 && <div className="px-5 pb-5 text-sm text-slate-500">No outgoing requests.</div>}
          <ul className="px-5 pb-4 space-y-2">
            {requests.outgoing.map((r) => (
              <li key={r.id} className="rounded-lg bg-white border border-slate-200 px-3.5 py-2.5" data-testid={`xfer-outgoing-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{r.student_name}</div>
                    <div className="text-xs text-slate-500 truncate">to {r.to_corporate_name}{r.to_school_name ? ` · ${r.to_school_name}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_CLS[r.status]}`}>{r.status}</span>
                    {r.status === "pending" && (
                      <button onClick={() => resolve(r.id, "cancel")} data-testid={`xfer-cancel-${r.id}`} title="Cancel request"
                        className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 transition">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Dialog open={!!acceptFor} onOpenChange={(o) => { if (!o) setAcceptFor(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="xfer-accept-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-600" /> Accept transfer
            </DialogTitle>
            <DialogDescription>
              Welcome <strong>{acceptFor?.student_name}</strong> from {acceptFor?.from_corporate_name}. Pick which of your schools they will join.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <label className="block">
              <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Destination school</span>
              <select value={acceptSchool} onChange={(e) => { setAcceptSchool(e.target.value); setAcceptGrade(""); }}
                data-testid="xfer-accept-school" className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary">
                <option value="">Choose school…</option>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Grade (optional)</span>
              <select value={acceptGrade} onChange={(e) => setAcceptGrade(e.target.value)} disabled={!acceptSchool}
                data-testid="xfer-accept-grade" className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary disabled:opacity-50">
                <option value="">Assign later</option>
                {acceptGrades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </label>
          </div>
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => setAcceptFor(null)} data-testid="xfer-accept-cancel"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
              Cancel
            </button>
            <button type="button" onClick={accept} disabled={accepting || !acceptSchool} data-testid="xfer-accept-confirm"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 transition disabled:opacity-50">
              {accepting && <Loader2 className="h-4 w-4 animate-spin" />} Accept & transfer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
